// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  PublicIPAddress,
  PublicIPAddressesListCloudServicePublicIPAddressesOptionalParams,
  PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesOptionalParams,
  PublicIPAddressesListAllOptionalParams,
  PublicIPAddressesListOptionalParams,
  PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesOptionalParams,
  PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesOptionalParams,
  PublicIPAddressesGetCloudServicePublicIPAddressOptionalParams,
  PublicIPAddressesGetCloudServicePublicIPAddressResponse,
  PublicIPAddressesDeleteOptionalParams,
  PublicIPAddressesGetOptionalParams,
  PublicIPAddressesGetResponse,
  PublicIPAddressesCreateOrUpdateOptionalParams,
  PublicIPAddressesCreateOrUpdateResponse,
  TagsObject,
  PublicIPAddressesUpdateTagsOptionalParams,
  PublicIPAddressesUpdateTagsResponse,
  PublicIPAddressesDdosProtectionStatusOptionalParams,
  PublicIPAddressesDdosProtectionStatusResponse,
  ReserveCloudServicePublicIpAddressRequest,
  PublicIPAddressesReserveCloudServicePublicIpAddressOptionalParams,
  PublicIPAddressesReserveCloudServicePublicIpAddressResponse,
  DisassociateCloudServicePublicIpRequest,
  PublicIPAddressesDisassociateCloudServiceReservedPublicIpOptionalParams,
  PublicIPAddressesDisassociateCloudServiceReservedPublicIpResponse,
  PublicIPAddressesGetVirtualMachineScaleSetPublicIPAddressOptionalParams,
  PublicIPAddressesGetVirtualMachineScaleSetPublicIPAddressResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a PublicIPAddresses. */
export interface PublicIPAddresses {
  /**
   * Gets information about all public IP addresses on a cloud service level.
   * @param resourceGroupName The name of the resource group.
   * @param cloudServiceName The name of the cloud service.
   * @param options The options parameters.
   */
  listCloudServicePublicIPAddresses(
    resourceGroupName: string,
    cloudServiceName: string,
    options?: PublicIPAddressesListCloudServicePublicIPAddressesOptionalParams,
  ): PagedAsyncIterableIterator<PublicIPAddress>;
  /**
   * Gets information about all public IP addresses in a role instance IP configuration in a cloud
   * service.
   * @param resourceGroupName The name of the resource group.
   * @param cloudServiceName The name of the cloud service.
   * @param roleInstanceName The name of role instance.
   * @param networkInterfaceName The network interface name.
   * @param ipConfigurationName The IP configuration name.
   * @param options The options parameters.
   */
  listCloudServiceRoleInstancePublicIPAddresses(
    resourceGroupName: string,
    cloudServiceName: string,
    roleInstanceName: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    options?: PublicIPAddressesListCloudServiceRoleInstancePublicIPAddressesOptionalParams,
  ): PagedAsyncIterableIterator<PublicIPAddress>;
  /**
   * Gets all the public IP addresses in a subscription.
   * @param options The options parameters.
   */
  listAll(
    options?: PublicIPAddressesListAllOptionalParams,
  ): PagedAsyncIterableIterator<PublicIPAddress>;
  /**
   * Gets all public IP addresses in a resource group.
   * @param resourceGroupName The name of the resource group.
   * @param options The options parameters.
   */
  list(
    resourceGroupName: string,
    options?: PublicIPAddressesListOptionalParams,
  ): PagedAsyncIterableIterator<PublicIPAddress>;
  /**
   * Gets information about all public IP addresses on a virtual machine scale set level.
   * @param resourceGroupName The name of the resource group.
   * @param virtualMachineScaleSetName The name of the virtual machine scale set.
   * @param options The options parameters.
   */
  listVirtualMachineScaleSetPublicIPAddresses(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    options?: PublicIPAddressesListVirtualMachineScaleSetPublicIPAddressesOptionalParams,
  ): PagedAsyncIterableIterator<PublicIPAddress>;
  /**
   * Gets information about all public IP addresses in a virtual machine IP configuration in a virtual
   * machine scale set.
   * @param resourceGroupName The name of the resource group.
   * @param virtualMachineScaleSetName The name of the virtual machine scale set.
   * @param virtualmachineIndex The virtual machine index.
   * @param networkInterfaceName The network interface name.
   * @param ipConfigurationName The IP configuration name.
   * @param options The options parameters.
   */
  listVirtualMachineScaleSetVMPublicIPAddresses(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    virtualmachineIndex: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    options?: PublicIPAddressesListVirtualMachineScaleSetVMPublicIPAddressesOptionalParams,
  ): PagedAsyncIterableIterator<PublicIPAddress>;
  /**
   * Get the specified public IP address in a cloud service.
   * @param resourceGroupName The name of the resource group.
   * @param cloudServiceName The name of the cloud service.
   * @param roleInstanceName The role instance name.
   * @param networkInterfaceName The name of the network interface.
   * @param ipConfigurationName The name of the IP configuration.
   * @param publicIpAddressName The name of the public IP Address.
   * @param options The options parameters.
   */
  getCloudServicePublicIPAddress(
    resourceGroupName: string,
    cloudServiceName: string,
    roleInstanceName: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesGetCloudServicePublicIPAddressOptionalParams,
  ): Promise<PublicIPAddressesGetCloudServicePublicIPAddressResponse>;
  /**
   * Deletes the specified public IP address.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>>;
  /**
   * Deletes the specified public IP address.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesDeleteOptionalParams,
  ): Promise<void>;
  /**
   * Gets the specified public IP address in a specified resource group.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesGetOptionalParams,
  ): Promise<PublicIPAddressesGetResponse>;
  /**
   * Creates or updates a static or dynamic public IP address.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameters supplied to the create or update public IP address operation.
   * @param options The options parameters.
   */
  beginCreateOrUpdate(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: PublicIPAddress,
    options?: PublicIPAddressesCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<PublicIPAddressesCreateOrUpdateResponse>,
      PublicIPAddressesCreateOrUpdateResponse
    >
  >;
  /**
   * Creates or updates a static or dynamic public IP address.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameters supplied to the create or update public IP address operation.
   * @param options The options parameters.
   */
  beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: PublicIPAddress,
    options?: PublicIPAddressesCreateOrUpdateOptionalParams,
  ): Promise<PublicIPAddressesCreateOrUpdateResponse>;
  /**
   * Updates public IP address tags.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameters supplied to update public IP address tags.
   * @param options The options parameters.
   */
  updateTags(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: TagsObject,
    options?: PublicIPAddressesUpdateTagsOptionalParams,
  ): Promise<PublicIPAddressesUpdateTagsResponse>;
  /**
   * Gets the Ddos Protection Status of a Public IP Address
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param options The options parameters.
   */
  beginDdosProtectionStatus(
    resourceGroupName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesDdosProtectionStatusOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<PublicIPAddressesDdosProtectionStatusResponse>,
      PublicIPAddressesDdosProtectionStatusResponse
    >
  >;
  /**
   * Gets the Ddos Protection Status of a Public IP Address
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param options The options parameters.
   */
  beginDdosProtectionStatusAndWait(
    resourceGroupName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesDdosProtectionStatusOptionalParams,
  ): Promise<PublicIPAddressesDdosProtectionStatusResponse>;
  /**
   * Reserves the specified Cloud Service Public IP by switching its allocation method to Static. If
   * rollback is requested, reverts the allocation method to Dynamic.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameter that define which Public IP Address should be associated in place of
   *                   given Public IP Address.
   * @param options The options parameters.
   */
  beginReserveCloudServicePublicIpAddress(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: ReserveCloudServicePublicIpAddressRequest,
    options?: PublicIPAddressesReserveCloudServicePublicIpAddressOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<PublicIPAddressesReserveCloudServicePublicIpAddressResponse>,
      PublicIPAddressesReserveCloudServicePublicIpAddressResponse
    >
  >;
  /**
   * Reserves the specified Cloud Service Public IP by switching its allocation method to Static. If
   * rollback is requested, reverts the allocation method to Dynamic.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameter that define which Public IP Address should be associated in place of
   *                   given Public IP Address.
   * @param options The options parameters.
   */
  beginReserveCloudServicePublicIpAddressAndWait(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: ReserveCloudServicePublicIpAddressRequest,
    options?: PublicIPAddressesReserveCloudServicePublicIpAddressOptionalParams,
  ): Promise<PublicIPAddressesReserveCloudServicePublicIpAddressResponse>;
  /**
   * Disassociates the Cloud Service reserved Public IP and associates the specified Standalone Public IP
   * to the same Cloud Service frontend.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameter that define which Public IP Address should be associated in place of
   *                   given Public IP Address.
   * @param options The options parameters.
   */
  beginDisassociateCloudServiceReservedPublicIp(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: DisassociateCloudServicePublicIpRequest,
    options?: PublicIPAddressesDisassociateCloudServiceReservedPublicIpOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<PublicIPAddressesDisassociateCloudServiceReservedPublicIpResponse>,
      PublicIPAddressesDisassociateCloudServiceReservedPublicIpResponse
    >
  >;
  /**
   * Disassociates the Cloud Service reserved Public IP and associates the specified Standalone Public IP
   * to the same Cloud Service frontend.
   * @param resourceGroupName The name of the resource group.
   * @param publicIpAddressName The name of the public IP address.
   * @param parameters Parameter that define which Public IP Address should be associated in place of
   *                   given Public IP Address.
   * @param options The options parameters.
   */
  beginDisassociateCloudServiceReservedPublicIpAndWait(
    resourceGroupName: string,
    publicIpAddressName: string,
    parameters: DisassociateCloudServicePublicIpRequest,
    options?: PublicIPAddressesDisassociateCloudServiceReservedPublicIpOptionalParams,
  ): Promise<PublicIPAddressesDisassociateCloudServiceReservedPublicIpResponse>;
  /**
   * Get the specified public IP address in a virtual machine scale set.
   * @param resourceGroupName The name of the resource group.
   * @param virtualMachineScaleSetName The name of the virtual machine scale set.
   * @param virtualmachineIndex The virtual machine index.
   * @param networkInterfaceName The name of the network interface.
   * @param ipConfigurationName The name of the IP configuration.
   * @param publicIpAddressName The name of the public IP Address.
   * @param options The options parameters.
   */
  getVirtualMachineScaleSetPublicIPAddress(
    resourceGroupName: string,
    virtualMachineScaleSetName: string,
    virtualmachineIndex: string,
    networkInterfaceName: string,
    ipConfigurationName: string,
    publicIpAddressName: string,
    options?: PublicIPAddressesGetVirtualMachineScaleSetPublicIPAddressOptionalParams,
  ): Promise<PublicIPAddressesGetVirtualMachineScaleSetPublicIPAddressResponse>;
}
